import { ProductCard } from '@src/components/product-card/product-card.component'
import {
  getInitialSelectedVariant,
  getSelectedOptionsForProductCard,
} from '@src/components/product-card/product-card.helpers'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Button, BUTTON_SIZE, BUTTON_TYPES, LoaderWrapper } from '@src/quince-components/src'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo } from 'react'

import { WishlistItem } from '../../wishlist.types'

import styles from './wishlist-item-list.module.scss'

function WishlistItemList({
  items,
  isLoading = false,
  sectionConfig: { tracker },
  isSelfWishlist,
}: {
  items: WishlistItem[] | undefined
  isLoading?: boolean
  sectionConfig: { tracker?: string }
  isSelfWishlist?: boolean
}) {
  const deviceInfo = useDeviceInfo()

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const reversedWishlistItems = useMemo(() => {
    return items ? [...items].reverse() : []
  }, [items])

  // Note: Scrolling to top always whenever data refreshes.
  useEffect(() => {
    if (isLoading) {
      window.scrollTo({
        top: 0,
        left: 0,
        // @i18n-ignore
        behavior: 'smooth',
      })
    }
  }, [isLoading])

  const { t } = useTranslation('wishlist')

  return (
    <LoaderWrapper showLoader={isLoading}>
      <div className={styles.wishListContainer}>
        {reversedWishlistItems.length > 0 ? (
          <div className={styles.wishListWrapper}>
            {reversedWishlistItems.map((productCard, index) => {
              const selectedCardVariant = getInitialSelectedVariant(productCard.product)
              const selectedColor = selectedCardVariant?.displayConfig.value

              return (
                <div
                  key={`${productCard.product.productId}_${selectedColor}`}
                  className={styles.wishListItemWrapper}
                  onClick={() => {
                    setFindingMethod[FINDING_METHOD_AGGREGATE.WISHLIST]?.()
                  }}
                >
                  <ProductCard
                    productCard={productCard}
                    maxWidth={maxWidth}
                    className={classNames(styles.productCard)}
                    tracker={tracker && `${tracker}__wishlist__${index}`}
                    hideBuyNowIcon
                    showDeleteBtn={isSelfWishlist}
                    optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                    shouldShowMore
                    productClickTrackConfig={{
                      trackInternal: true,
                      trackGA: true,
                      // @i18n-ignore
                      category: 'product-list',
                      action: `${tracker ?? 'wishlist'}__click_product_wishlist`,
                      value: productCard.product.relatedIds.shopifyId.toString(),
                      context: {
                        contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
                        contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.SUBCOLLECTION_DISPLAY,
                        // @i18n-ignore
                        contentContainerDisplayName: 'Wishlist',
                        findingMethod: FINDING_METHOD_AGGREGATE.WISHLIST,
                      },
                    }}
                  />
                  {deviceInfo.deviceType === DEVICE_TYPES.DESKTOP ? (
                    <div className={styles.addToCartBtnContainer}>
                      <Button
                        type={BUTTON_TYPES.OUTLINED}
                        onClick={() => {
                          productBuyNowStateManager.showBuyNowPopUp({
                            productCard: productCard,
                            selectedOptions: getSelectedOptionsForProductCard(selectedCardVariant),
                          })
                        }}
                        className={styles.addToCartBtn}
                      >
                        {t('add_to_cart')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : (
          <EmptyWishList />
        )}
      </div>
    </LoaderWrapper>
  )
}

function EmptyWishList() {
  const router = useRouter()
  const { t } = useTranslation('wishlist')
  return (
    <div className={styles.emptyWishlist}>
      <h3 className={styles.emptyWishlist__title}>{t('your_wishlist_is_empty')}</h3>
      <Button
        onClick={() => {
          router.push('/women')
        }}
        round={true}
        size={BUTTON_SIZE.MEDIUM}
        type={BUTTON_TYPES.SECONDARY}
        className={styles.emptyWishlist__cta}
      >
        {t('shop_now')}
      </Button>
    </div>
  )
}

export default WishlistItemList
